
import React, { useEffect, useState } from "react";
import axios from "axios";
import endPoint from "../../websitecomponents/redux/EndPoint/endpoint";
import { toast } from "react-toastify";

const NewOrder = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔥 Filters
  const [searchOrderId, setSearchOrderId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  // ===============================
  // Fetch All Orders
  // ===============================
  const fetchOrders = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("SellerTokenData");

      const res = await axios.get(endPoint.SellerOrder, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data.success) {
        setOrders(res.data.orders || []);
      }
    } catch (err) {
      console.error("Order fetch error:", err);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // ===============================
  // Search Orders by OrderID / Status
  // ===============================
  const searchOrders = async () => {
    try {
      const token = localStorage.getItem("SellerTokenData");

      if (!searchOrderId && !selectedStatus) {
        fetchOrders();
        return;
      }

      setLoading(true);

      let query = [];

      if (searchOrderId.trim()) {
        query.push(`orderID=${searchOrderId}`);
      }

      if (selectedStatus) {
        query.push(`status=${selectedStatus}`);
      }

      const url = `${endPoint.SellerOrderByIdorStatus}?${query.join("&")}`;

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data.success) {
        setOrders(res.data.orders || []);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error(error);
      setOrders([]);
      toast.error("No matching orders found");
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // Update Order Status
  // ===============================
  const updateStatus = async (orderID, newStatus) => {
    try {
      const token = localStorage.getItem("SellerTokenData");

      const res = await axios.put(
        `${endPoint.UpdateOrderStatus}/${orderID}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data?.success) {
        toast.success("Status updated successfully");
        searchOrders();
      } else {
        toast.error("Status update failed");
      }
    } catch (err) {
      console.error("Status update error:", err);
      toast.error("Failed to update status");
    }
  };

  // ===============================
  // Clear Filters
  // ===============================
  const handleClear = () => {
    setSearchOrderId("");
    setSelectedStatus("");
    fetchOrders();
  };

  // ===============================
  // UI
  // ===============================
  return (
    <div className="p-4 md:p-6">
      {/* ========================= */}
      {/* TOP FILTER SECTION */}
      {/* ========================= */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">

        <div className="flex flex-wrap gap-3 items-end">
          {/* Order ID Search */}
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Order ID</label>
            <input
              type="text"
              placeholder="Search by Order ID"
              value={searchOrderId}
              onChange={(e) => setSearchOrderId(e.target.value)}
              className="w-64 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none"
            />
          </div>

          {/* Status Dropdown */}
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-52 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none"
            >
              <option value="">All Status</option>
              <option value="received">Received</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Search */}
          <button
            onClick={searchOrders}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Search
          </button>

          {/* Clear */}
          <button
            onClick={handleClear}
            className="px-5 py-2 rounded-lg border bg-gray-100 hover:bg-gray-200"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* ========================= */}
      {/* TABLE */}
      {/* ========================= */}
      <div className="bg-white border shadow rounded-lg overflow-x-auto">
        {loading ? (
          <div className="py-20 flex justify-center">
            <div className="h-12 w-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="py-20 text-center">
            <h3 className="text-lg font-medium text-gray-700">
              No Orders Found
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              Seller orders will appear here
            </p>
          </div>
        ) : (
          <table className="min-w-full w-max border-collapse text-sm md:text-base">
            <thead className="bg-gray-100">
              <tr>
                {[
                  "Order ID",
                  "Customer ID",
                  "Items",
                  "Total",
                  "Payment Mode",
                  "Shipping Cost",
                  "Delivery Address",
                  "Created At",
                  "Status",
                ].map((header) => (
                  <th
                    key={header}
                    className="border px-3 py-3 text-left whitespace-nowrap"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {orders.map((order) => (
                <tr key={order.orderID} className="hover:bg-gray-50 align-top">
                  {/* Order ID */}
                  <td className="border px-3 py-2 font-semibold">
                    {order.orderID}
                  </td>

                  {/* Customer */}
                  <td className="border px-3 py-2">
                    {order.customer?._id || "N/A"}
                  </td>

                  {/* Items */}
                  <td className="border px-3 py-2">
                    <table className="w-full border text-xs md:text-sm">
                      <thead className="bg-gray-200">
                        <tr>
                          <th className="border px-2 py-1">Image</th>
                          <th className="border px-2 py-1">Product</th>
                          <th className="border px-2 py-1">Qty</th>
                          <th className="border px-2 py-1">Price</th>
                        </tr>
                      </thead>

                      <tbody>
                        {order?.items?.map((item, i) => (
                          <tr key={i}>
                            <td className="border px-2 py-1">
                              {item.productImage ? (
                                <img
                                  src={item.productImage}
                                  alt={item.title}
                                  className="w-12 h-12 object-cover rounded"
                                />
                              ) : (
                                <div className="w-12 h-12 flex items-center justify-center text-xs text-gray-400 bg-gray-100 rounded">
                                  No Image
                                </div>
                              )}
                            </td>

                            <td className="border px-2 py-1">
                              {item.title || "-"}
                            </td>

                            <td className="border px-2 py-1 text-center">
                              {item.quantity}
                            </td>

                            <td className="border px-2 py-1">
                              ₹{item.totalPrice}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>

                  {/* Total */}
                  <td className="border px-3 py-2 font-bold text-green-600">
                    ₹
                    {order.items.reduce(
                      (sum, item) => sum + (item.totalPrice || 0),
                      0,
                    )}
                  </td>

                  {/* Payment */}
                  <td className="border px-3 py-2">
                    {order.paymentMode || "N/A"}
                  </td>

                  {/* Shipping */}
                  <td className="border px-3 py-2">
                    ₹{order.shippingCost || 0}
                  </td>

                  {/* Address */}
                  <td className="border px-3 py-2 text-sm leading-6">
                    <div className="font-semibold">
                      {order.address?.name || "-"}
                    </div>
                    <div>{order.address?.location || "-"}</div>
                    <div>
                      {order.address?.city || "-"},{" "}
                      {order.address?.state || "-"}
                    </div>
                    <div>{order.address?.pincode || "-"}</div>
                    <div>📞 {order.address?.phone || "-"}</div>
                  </td>

                  {/* Date */}
                  <td className="border px-3 py-2 text-sm">
                    {new Date(order.createdAt).toLocaleDateString()}
                    <br />
                    {new Date(order.createdAt).toLocaleTimeString()}
                  </td>

                  {/* Status Dropdown */}
                  <td className="border px-3 py-2">
                    <select
                      value={order.status}
                      onChange={(e) =>
                        updateStatus(order.orderID, e.target.value)
                      }
                      className="px-3 py-2 rounded border text-sm font-medium"
                    >
                      <option value="received">Received</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default NewOrder;
